import { normalizeOperationsRealtimeMessage, type OperationsRealtimeEvent } from '@/lib/websocket/operationsRealtime';

type UnknownRecord = Record<string, unknown>;

export interface SmartTaskRealtimeScope {
  workItemIds?: Array<string | number | null | undefined>;
  appointmentIds?: Array<string | number | null | undefined>;
  jobIds?: Array<string | number | null | undefined>;
  groupIds?: Array<string | number | null | undefined>;
  entityIds?: Array<string | number | null | undefined>;
  extraIds?: Array<string | number | null | undefined>;
}

export interface NormalizedRealtimeEnvelope {
  message: OperationsRealtimeEvent | UnknownRecord;
  payload: UnknownRecord | null;
  targetIds: string[];
}

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

function normalizeId(value: unknown): string | null {
  const normalized = String(value ?? '').trim();
  return normalized ? normalized : null;
}

function appendId(targetIds: Set<string>, value: unknown): void {
  const normalized = normalizeId(value);
  if (normalized) {
    targetIds.add(normalized);
  }
}

function appendIds(targetIds: Set<string>, values: unknown): void {
  if (!Array.isArray(values)) {
    appendId(targetIds, values);
    return;
  }

  values.forEach((value) => appendId(targetIds, value));
}

export function getRealtimeEnvelope(data: unknown): NormalizedRealtimeEnvelope | null {
  const normalizedMessage = normalizeOperationsRealtimeMessage(data);
  const rawMessage = asRecord(data);
  const message = normalizedMessage
    ? normalizedMessage as unknown as UnknownRecord
    : rawMessage;

  if (!message) {
    return null;
  }

  const payload = asRecord((normalizedMessage?.payload as unknown) ?? rawMessage?.payload) || null;
  const targetIds = new Set<string>();

  appendId(targetIds, message.entity_id);
  appendId(targetIds, message.work_item_id);
  appendId(targetIds, message.appointment_id);
  appendId(targetIds, message.job_id);
  appendId(targetIds, message.parent_job_id);
  appendId(targetIds, message.group_id);
  appendIds(targetIds, message.appointment_ids);

  appendId(targetIds, payload?.entity_id);
  appendId(targetIds, payload?.work_item_id);
  appendId(targetIds, payload?.appointment_id);
  appendId(targetIds, payload?.job_id);
  appendId(targetIds, payload?.parent_job_id);
  appendId(targetIds, payload?.group_id);
  appendId(targetIds, payload?.vehicle_id);
  appendIds(targetIds, payload?.appointment_ids);

  return {
    message,
    payload,
    targetIds: Array.from(targetIds),
  };
}

export function getRealtimeScopeIds(scope: SmartTaskRealtimeScope): Set<string> {
  const targetIds = new Set<string>();

  appendIds(targetIds, scope.workItemIds);
  appendIds(targetIds, scope.appointmentIds);
  appendIds(targetIds, scope.jobIds);
  appendIds(targetIds, scope.groupIds);
  appendIds(targetIds, scope.entityIds);
  appendIds(targetIds, scope.extraIds);

  return targetIds;
}

export function doesRealtimeMessageAffectScope(data: unknown, scope: SmartTaskRealtimeScope): boolean {
  const envelope = getRealtimeEnvelope(data);
  if (!envelope) {
    return false;
  }

  const scopeIds = getRealtimeScopeIds(scope);
  if (scopeIds.size === 0) {
    return false;
  }

  return envelope.targetIds.some((targetId) => scopeIds.has(targetId));
}